/**
 * Wave 3 (v2.0b) — meal planner + shopping diff shared contracts.
 *
 * A meal is a planned dinner with a date and an ingredient list. Confirming a
 * meal draft persists the meal and pushes its still-missing ingredients onto
 * the shared shopping list — the "shopping diff".
 *
 * `diffIngredients` is the single source of truth for that diff, shared by:
 *   - apps/api meals.service (authoritative add at confirm)
 *   - apps/web DraftReviewCard (live preview of what will be added)
 *
 * Matching is deterministic and best-effort: text is lowercased + whitespace-
 * collapsed, then an ingredient counts as "already on the list" when a current
 * (non-removed) item's text equals it or contains it as a substring (e.g. list
 * item "økologiske egg" covers ingredient "egg"). It never reads structured
 * data — the shopping list is free text — so it's intentionally conservative:
 * a near-miss surfaces as "needed" rather than being silently dropped.
 */

/** A persisted planned meal. */
export interface Meal {
  id: string;
  /** YYYY-MM-DD (Europe/Oslo) the meal is planned for, or null when undated. */
  date: string | null;
  title: string;
  ingredients: string[];
  notes: string | null;
  created_by: string;
  /** ISO timestamp with Europe/Oslo offset. */
  created_at: string;
}

/** Result of comparing a meal's ingredients against the current shopping list. */
export interface IngredientDiff {
  /** Ingredients NOT found on the list — these get added on confirm. */
  needed: string[];
  /** Ingredients already covered by an existing list item. */
  have: string[];
}

/** Lowercase + collapse internal whitespace for a forgiving comparison. */
export function normalizeIngredient(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Split a meal's ingredient list into {needed, have} against the current
 * shopping-list item texts. Duplicates within `ingredients` are collapsed
 * (first spelling wins); blank entries are dropped. Order of `ingredients`
 * is preserved within each bucket.
 */
export function diffIngredients(ingredients: string[], existingTexts: string[]): IngredientDiff {
  const existing = existingTexts.map(normalizeIngredient).filter((t) => t.length > 0);
  const needed: string[] = [];
  const have: string[] = [];
  const seen = new Set<string>();

  for (const raw of ingredients) {
    const ingredient = raw.trim();
    if (!ingredient) continue;
    const norm = normalizeIngredient(ingredient);
    if (seen.has(norm)) continue;
    seen.add(norm);

    const present = existing.some((item) => item === norm || item.includes(norm));
    (present ? have : needed).push(ingredient);
  }

  return { needed, have };
}
